import type { Match, Standing, Tournament as UiTournament } from "@dowty/tournament-ui";
import type { Tournament as LegacyTournament, TournamentMatch } from "../../../services/beerpongService";
import { BracketType, TournamentStatus, TournamentStyle, TournamentVisibility } from "../../../generated/bpong_pb";

const roundOf = (match: TournamentMatch) => Math.max(1, match.roundNumber ?? 1);

const formatOf = (style: TournamentStyle): UiTournament["stages"][number]["format"] => {
    if (style === TournamentStyle.TOURNAMENT_STYLE_ROUND_ROBIN) return "round_robin";
    return style === TournamentStyle.TOURNAMENT_STYLE_DOUBLE_ELIMINATION ? "double_elim" : "single_elim";
};

const sideOf = (match: TournamentMatch): Match["side"] => {
    if (match.bracketType === BracketType.BRACKET_TYPE_LOSERS) return "L";
    if (match.bracketType === BracketType.BRACKET_TYPE_FINALS) return "GF";
    return "W";
};

const buildMatchBuckets = (matches: TournamentMatch[]) => {
    const ordered = [...matches].sort((a, b) => roundOf(a) - roundOf(b) || a.matchId - b.matchId);
    const positions = new Map<number, number>(), buckets = new Map<string, TournamentMatch[]>();
    for (const match of ordered) {
        const key = `${sideOf(match)}:${roundOf(match)}`, bucket = buckets.get(key) ?? [];
        bucket.push(match); buckets.set(key, bucket); positions.set(match.matchId, bucket.length);
    }
    return { ordered, positions, buckets };
};

const targetAtPosition = <T,>(targets: T[], position: number, sourceCount: number) =>
    targets[Math.min(targets.length - 1, Math.floor((position - 1) * targets.length / sourceCount))];

const matchStatus = (match: TournamentMatch, participantsPresent: boolean, walkover: boolean): Match["status"] => {
    if (walkover) return "Bye";
    if (match.winnerTeamId) return "Done";
    return participantsPresent ? "Ready" : "Pending";
};

const mapMatch = (match: TournamentMatch, format: UiTournament["stages"][number]["format"], stageId: number, positions: Map<number, number>, buckets: Map<string, TournamentMatch[]>): Match => {
    const side = sideOf(match);
    const round = roundOf(match);
    const position = positions.get(match.matchId) ?? 1;
    const ownNext = buckets.get(`${side}:${round + 1}`) ?? [];
    const finalMatches = format === "double_elim" && side !== "GF" ? buckets.get("GF:1") ?? [] : [];
    const left = ownNext.length ? ownNext : finalMatches;
    const winnerTarget = side === "GF" || left.length === 0 ? null : targetAtPosition(left, position, buckets.get(`${side}:${round}`)?.length ?? 1);
    const losers = side === "W" && format === "double_elim" ? buckets.get(`L:${round}`) ?? [] : [];
    const sourceCount = buckets.get(`W:${round}`)?.length ?? 1;
    const loserTarget = losers.length ? targetAtPosition(losers, position, sourceCount) : null;
    const participantsPresent = match.teamAId > 0 && match.teamBId > 0;
    const walkover = match.isCancelled || (match.teamAId > 0 && match.teamAId === match.teamBId);
    const status = matchStatus(match, participantsPresent, walkover);
    return {
        id: match.matchId, stageId, groupNo: null, round, position, side,
        entrantAId: match.teamAId > 0 ? match.teamAId : null,
        entrantBId: match.teamBId > 0 && match.teamBId !== match.teamAId ? match.teamBId : null,
        scoreA: null, scoreB: null, winnerId: match.winnerTeamId, status, walkover,
        winnerTo: winnerTarget ? { matchId: winnerTarget.matchId, slot: "A" } : null,
        loserTo: loserTarget ? { matchId: loserTarget.matchId, slot: "B" } : null,
        tableLabel: match.isCancelled ? "Cancelled" : null,
    };
};

const connectLoserRounds = (matches: Match[], buckets: Map<string, TournamentMatch[]>) => {
    for (const result of matches) {
        if (result.side !== "L") continue;
        const next = buckets.get(`L:${result.round + 1}`) ?? [], current = buckets.get(`L:${result.round}`) ?? [];
        const target = next[Math.min(next.length - 1, Math.floor((result.position - 1) * next.length / current.length))];
        if (target && next.length) result.winnerTo = { matchId: target.matchId, slot: "A" };
    }
};

const buildUiMatches = (source: LegacyTournament, format: UiTournament["stages"][number]["format"], stageId: number) => {
    const { ordered, positions, buckets } = buildMatchBuckets(source.matches);
    const matches = ordered.map((match) => mapMatch(match, format, stageId, positions, buckets));
    // Feed loser-round connectors to the same inferred slot order used for winners.
    if (format === "double_elim") connectLoserRounds(matches, buckets);
    return matches;
};

const updateStandingStats = (match: TournamentMatch, stats: Map<number, { played: number; wins: number; losses: number }>) => {
    const winner = stats.get(match.winnerTeamId!);
    const loserId = match.winnerTeamId === match.teamAId ? match.teamBId : match.teamAId;
    const loser = stats.get(loserId);
    if (winner) { winner.played += 1; winner.wins += 1; }
    if (loser && loserId !== match.winnerTeamId) { loser.played += 1; loser.losses += 1; }
};

const visibilityOf = (visibility: TournamentVisibility): UiTournament["visibility"] => {
    switch (visibility) {
        case TournamentVisibility.TOURNAMENT_VISIBILITY_PASSWORD: return "password";
        case TournamentVisibility.TOURNAMENT_VISIBILITY_PRIVATE: return "private";
        case TournamentVisibility.TOURNAMENT_VISIBILITY_FRIENDS: return "friends";
        default: return "public";
    }
};

export const toTournamentUi = (source: LegacyTournament): UiTournament => {
    const format = formatOf(source.style);
    const stageId = source.tournamentId;
    const matches = buildUiMatches(source, format, stageId);
    const stats = new Map(source.teams.map((team) => [team.teamId, { played: 0, wins: 0, losses: 0 }]));
    source.matches.filter((match) => !match.isCancelled && match.winnerTeamId).forEach((match) => updateStandingStats(match, stats));
    const standings: Standing[] = Array.from(stats.entries())
        .map(([entrantId, score]) => ({ stageId, groupNo: null, entrantId, rank: 0, played: score.played, wins: score.wins, draws: 0, losses: score.losses, scoreFor: 0, scoreAgainst: 0, points: score.wins, tieBreaks: [] }))
        .sort((a, b) => b.points - a.points || b.wins - a.wins || a.entrantId - b.entrantId)
        .map((standing, index) => ({ ...standing, rank: index + 1 }));
    const finished = source.status === TournamentStatus.TOURNAMENT_STATUS_COMPLETED || source.status === TournamentStatus.TOURNAMENT_STATUS_CANCELLED;
    const status = finished ? "Completed" : "Running";
    let stageStatus: "Completed" | "Running" | "Pending" = source.matches.length ? "Running" : "Pending";
    if (finished) stageStatus = "Completed";

    return {
        id: source.tournamentId, name: source.tournamentName, discipline: "Beer pong", app: "saufdings", contextId: source.eventId,
        settings: { scoring: "winner_only", allowDraws: false, resultEntry: "organizers", tieBreakOrder: ["points", "wins"], seedMode: "manual", tables: null },
        status, visibility: visibilityOf(source.visibility), version: 1,
        stages: [{ id: stageId, order: 1, name: format === "round_robin" ? "League table" : "Tournament bracket", format, status: stageStatus }],
        entrants: source.teams.map((team, index) => ({ id: team.teamId, name: team.teamName, seed: index + 1, groupNo: null, withdrawn: team.isEliminated, members: team.members.map(({ userId, displayName }) => ({ userId, displayName })) })),
        matches, standings, me: { canOrganize: source.isOwner, canReportMatchIds: [], entrantId: null },
    };
};
